/**
 * ProfileTab — identity, subscription, data/privacy actions, sign-out.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { ApiError, authFetchJson, authFetchRaw, getSubscriptionStatus } from "@/lib/api";
import type { SubscriptionStatus } from "@/lib/api";
import { useAuth } from "@/lib/hooks/useAuth";
import { clearPendingUploads } from "@/lib/pendingUploads";
import { signOut } from "@/lib/auth";
import { carmenAuth } from "@/lib/carmenAuth";
import { BillingSection } from "@/routes/account/billing/BillingSection";
import { daysUntilInBrussels } from "@/lib/format";

const STATUS_LABELS: Record<string, { label: string; classes: string }> = {
  trialing: { label: "Proefperiode", classes: "bg-rose-100 text-rose-800" },
  active: { label: "Actief", classes: "bg-green-100 text-green-800" },
  past_due: { label: "Betaling mislukt", classes: "bg-red-100 text-red-800" },
  canceled: { label: "Opgezegd", classes: "bg-slate-100 text-slate-800" },
};

const ROLE_LABELS: Record<string, string> = {
  superuser: "Superuser",
  hospital_admin: "Organisatiebeheerder",
  department_admin: "Afdelingsbeheerder",
  association_admin: "Associatiebeheerder",
  afdeling_secretary: "Afdelingssecretaris",
  it_admin: "IT-beheerder",
  user: "Gebruiker",
};

const DELETE_CONFIRMATION = "VERWIJDEREN";

export function ProfileTab() {
  const nav = useNavigate();
  const { accessRole } = useAuth();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [actionLoading, setActionLoading] = useState("");
  const [editingProfile, setEditingProfile] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [profileError, setProfileError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const session = await carmenAuth.getSession();
        if (!session) {
          nav("/login");
          return;
        }
        setEmail(session.user.email ?? "");
        setFullName(session.user.user_metadata.full_name ?? "");

        const sub = await getSubscriptionStatus();
        setSubscription(sub);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          nav("/login");
          return;
        }
        setError(err instanceof Error ? err.message : "Kon accountgegevens niet laden.");
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [nav]);

  const handleLogout = useCallback(async () => {
    await signOut();
    nav("/login");
  }, [nav]);

  const handleExport = useCallback(async () => {
    setActionLoading("export");
    setError("");
    try {
      const resp = await authFetchRaw("/api/account/export");
      if (!resp.ok) throw new Error("Export mislukt.");
      const data = (await resp.json()) as Record<string, unknown>;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "carmen-gegevens.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export mislukt.");
    } finally {
      setActionLoading("");
    }
  }, []);

  const handleBillingPortal = useCallback(async () => {
    setActionLoading("portal");
    setError("");
    try {
      const { url } = await authFetchJson<{ url: string }>("/api/billing/portal", {
        method: "POST",
      });
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kon betaalportaal niet openen.");
      setActionLoading("");
    }
  }, []);

  const startProfileEdit = useCallback(() => {
    setNameDraft(fullName);
    setProfileError("");
    setEditingProfile(true);
  }, [fullName]);

  const cancelProfileEdit = useCallback(() => {
    setProfileError("");
    setEditingProfile(false);
  }, []);

  const saveProfile = useCallback(async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      setProfileError("Naam mag niet leeg zijn.");
      return;
    }
    setActionLoading("profile");
    setProfileError("");
    try {
      await authFetchJson<{ ok: boolean; full_name: string }>("/api/account/profile", {
        method: "PATCH",
        body: JSON.stringify({ full_name: trimmed }),
      });
      await carmenAuth.getUser();
      setFullName(trimmed);
      setEditingProfile(false);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setActionLoading("");
    }
  }, [nameDraft]);

  const handleDeleteAccount = useCallback(async () => {
    setActionLoading("delete");
    setError("");
    try {
      await authFetchJson("/api/account", {
        method: "DELETE",
        headers: { "X-Account-Deletion-Confirmation": deleteConfirmation },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verwijderen mislukt.");
      setActionLoading("");
      setDeleteConfirmation("");
      setShowDeleteConfirm(false);
      return;
    }

    // The server-side deletion has succeeded. Local cleanup is best-effort:
    // a browser storage error must not make an already-deleted account look
    // as if it still exists.
    await Promise.allSettled([clearPendingUploads(), signOut()]);
    window.sessionStorage.removeItem("carmen_org_key");
    window.sessionStorage.removeItem("carmen-impersonating");
    window.localStorage.removeItem("carmen-auth-react");
    nav("/login");
  }, [deleteConfirmation, nav]);

  if (loading) {
    return <p className="text-sm text-slate-500">Laden…</p>;
  }

  const statusInfo = subscription
    ? STATUS_LABELS[subscription.status] ?? { label: "Onbekend", classes: "bg-slate-100 text-slate-600" }
    : null;

  return (
    <div className="space-y-6">
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {/* Profile */}
      <section className="glass-card rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">Profiel</h2>
          {!editingProfile ? (
            <button
              type="button"
              onClick={startProfileEdit}
              className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Bewerken
            </button>
          ) : null}
        </div>
        {!editingProfile ? (
          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium text-slate-500">E-mail</dt>
              <dd className="mt-0.5 text-slate-900">{email}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-500">Naam</dt>
              <dd className="mt-0.5 text-slate-900">{fullName || "—"}</dd>
            </div>
            {accessRole && accessRole !== "user" ? (
              <div>
                <dt className="text-xs font-medium text-slate-500">Rol</dt>
                <dd className="mt-0.5 text-slate-900">{ROLE_LABELS[accessRole] ?? accessRole}</dd>
              </div>
            ) : null}
          </dl>
        ) : (
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-medium text-slate-500">Naam</span>
              <input
                type="text"
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-100"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                autoComplete="name"
                autoFocus
              />
            </label>
            <div>
              <span className="block text-xs font-medium text-slate-500">E-mail</span>
              <p className="mt-0.5 text-sm text-slate-900">{email}</p>
              <p className="mt-0.5 text-xs text-slate-500">
                E-mailadres wijzigen? Neem contact op met support.
              </p>
            </div>
            {profileError ? (
              <p className="rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-700">
                {profileError}
              </p>
            ) : null}
            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={cancelProfileEdit}
                disabled={actionLoading === "profile"}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Annuleer
              </button>
              <button
                type="button"
                onClick={() => void saveProfile()}
                disabled={actionLoading === "profile"}
                className="rounded-xl bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-50"
              >
                {actionLoading === "profile" ? "Opslaan…" : "Opslaan"}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Subscription */}
      {subscription ? (
        <section className="glass-card rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-900">Abonnement</h2>
            {statusInfo ? (
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusInfo.classes}`}>
                {statusInfo.label}
              </span>
            ) : null}
          </div>
          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            {subscription.plan_label ? (
              <div>
                <dt className="text-xs font-medium text-slate-500">Plan</dt>
                <dd className="mt-0.5 text-slate-900">{subscription.plan_label}</dd>
              </div>
            ) : null}
            {subscription.trial_ends_at
              ? (() => {
                  const days = daysUntilInBrussels(subscription.trial_ends_at);
                  return days !== null && days > 0 ? (
                    <div>
                      <dt className="text-xs font-medium text-slate-500">Dagen proefperiode</dt>
                      <dd className="mt-0.5 text-slate-900">{days}</dd>
                    </div>
                  ) : null;
                })()
              : null}
            {typeof subscription.usage_count === "number" ? (
              <div>
                <dt className="text-xs font-medium text-slate-500">Gebruik deze maand</dt>
                <dd className="mt-0.5 text-slate-900">
                  {subscription.usage_count}
                  {subscription.usage_limit != null ? ` / ${subscription.usage_limit}` : ""}
                </dd>
              </div>
            ) : null}
          </dl>

          <button
            type="button"
            onClick={() => void handleBillingPortal()}
            disabled={actionLoading === "portal"}
            className="mt-4 rounded-xl bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-50"
          >
            {actionLoading === "portal" ? "Openen…" : "Beheer abonnement"}
          </button>
        </section>
      ) : null}

      <BillingSection />

      {/* Data & privacy */}
      <section className="glass-card rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-bold text-slate-900">Gegevens en privacy</h2>
        <div className="space-y-3 text-sm text-slate-700">
          <p>
            Onder de AVG heeft u het recht om uw gegevens in te zien, te exporteren en te
            laten verwijderen. Zie het{" "}
            <Link to="/privacy" className="text-rose-600 underline hover:text-rose-700">
              privacybeleid
            </Link>
            .
          </p>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void handleExport()}
            disabled={actionLoading === "export"}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {actionLoading === "export" ? "Exporteren…" : "Gegevens exporteren"}
          </button>
          {subscription?.organization_linked ? (
            <p className="self-center text-sm text-slate-500">
              Uw account hoort bij een organisatie. Verwijderen verloopt via uw
              organisatiebeheerder.
            </p>
          ) : (
            <button
              type="button"
              onClick={() => {
                setDeleteConfirmation("");
                setShowDeleteConfirm(true);
              }}
              className="rounded-xl border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
            >
              Account verwijderen
            </button>
          )}
        </div>
      </section>

      <button
        type="button"
        onClick={() => void handleLogout()}
        className="w-full rounded-xl bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-200"
      >
        Uitloggen
      </button>

      {showDeleteConfirm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-slate-900">Account verwijderen?</h3>
            <p className="mt-2 text-sm text-slate-600">
              Dit verwijdert permanent uw account, opnames, transcripties en andere
              appgegevens. Gegevens die wettelijk moeten worden bewaard, blijven alleen
              gedurende de verplichte termijn beschikbaar. Deze actie kan niet ongedaan
              worden gemaakt.
            </p>
            <label className="mt-4 block">
              <span className="text-sm font-medium text-slate-700">
                Typ <strong>{DELETE_CONFIRMATION}</strong> om te bevestigen
              </span>
              <input
                type="text"
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100"
              />
            </label>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setDeleteConfirmation("");
                  setShowDeleteConfirm(false);
                }}
                disabled={actionLoading === "delete"}
                className="flex-1 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Annuleer
              </button>
              <button
                type="button"
                onClick={() => void handleDeleteAccount()}
                disabled={
                  actionLoading === "delete" || deleteConfirmation !== DELETE_CONFIRMATION
                }
                className="flex-1 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading === "delete" ? "Verwijderen…" : "Ja, verwijder"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
